import React, { useState, useEffect } from 'react';
import './App.css';
import CategoryPage from './CategoryPage';
import { Testimonials } from './components/ui/unique-testimonial';
import { FluidDropdown } from './components/ui/fluid-dropdown';
import { MobileMenu } from './components/ui/MobileMenu';

interface Category {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  features: Feature[];
}

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: string;
}

const categories: Category[] = [
  {
    id: 'home-living',
    name: 'Home and Living',
    description: 'Everything you need to manage your home, from finding a house to organizing chores',
    color: '#E07A5F',
    icon: '🏠',
    features: [
      { id: 'find-house', title: 'Find a House', description: 'Discover your perfect home with advanced search and filters', icon: '🏠' },
      { id: 'buy-house', title: 'Buy a House', description: 'Complete guide for buying or renting your next property', icon: '🏡' },
      { id: 'split-assets', title: 'Split Assets', description: 'Organize and manage shared assets and expenses', icon: '💰' },
      { id: 'find-roomie', title: 'Find a Roomie', description: 'Connect with compatible roommates and housemates', icon: '👥' },
      { id: 'rent-room', title: 'Rent a Room', description: 'Manage room rentals and tenant agreements', icon: '🚪' },
      { id: 'house-chores', title: 'House Chores', description: 'Organize and track household tasks and responsibilities', icon: '🧹' },
      { id: 'plan-decor', title: 'Plan Décor', description: 'Design and plan your home decoration projects', icon: '🎨' },
      { id: 'plan-buy', title: 'Plan Things to Buy', description: 'Create shopping lists and track home purchases', icon: '🛒' },
      { id: 'diy-rooms', title: 'DIY Rooms', description: 'Plan and execute DIY room renovation projects', icon: '🔨' },
    ]
  },
  {
    id: 'work-career',
    name: 'Work, School & Career',
    description: 'Organize your professional life, education, and career development',
    color: '#3D5A6C',
    icon: '💼',
    features: [
      { id: 'organise-homework', title: 'Organise Homework', description: 'Track assignments, deadlines, and study schedules', icon: '📚' },
      { id: 'pre-plan-results', title: 'Pre-plan Results', description: 'Set goals and track academic performance', icon: '📊' },
      { id: 'career-planner', title: 'Career Planner', description: 'Plan your career path and professional development', icon: '💼' },
      { id: 'organise-school-bag', title: 'Organise School Bag', description: 'Keep track of school supplies and essentials', icon: '🎒' },
      { id: 'plan-leave', title: 'Plan Leave', description: 'Manage vacation days and time off requests', icon: '📅' },
      { id: 'work-life-balance', title: 'Plan Work/Life Balance', description: 'Balance professional and personal commitments', icon: '⚖️' },
      { id: 'plan-work-tasks', title: 'Plan Work Tasks', description: 'Organize and prioritize work projects and tasks', icon: '✅' },
      { id: 'plan-side-hustle', title: 'Plan Side Hustle', description: 'Manage side projects and additional income streams', icon: '🚀' },
    ]
  },
  {
    id: 'travel-lifestyle',
    name: 'Travel & Lifestyle',
    description: 'Plan perfect holidays, events, and special occasions',
    color: '#F4D35E',
    icon: '✈️',
    features: [
      { id: 'holiday-planner', title: 'Holiday Planner', description: 'Plan your perfect vacation from start to finish', icon: '✈️' },
      { id: 'holiday-packing', title: 'Holiday Packing', description: 'Create packing lists and never forget essentials', icon: '🧳' },
      { id: 'holiday-budgeting', title: 'Holiday Budgeting', description: 'Track expenses and stay within your travel budget', icon: '💳' },
      { id: 'holiday-itinerary', title: 'Holiday Itinerary', description: 'Plan daily activities and travel schedules', icon: '🗺️' },
      { id: 'outfit-planner', title: 'Outfit Planner', description: 'Plan outfits for trips and special occasions', icon: '👗' },
      { id: 'wedding-planner', title: 'Wedding Planner', description: 'Organize every detail of your special day', icon: '💍' },
      { id: 'table-planner', title: 'Table Planner', description: 'Design seating arrangements for events', icon: '🍽️' },
      { id: 'baby-events', title: 'Baby Events/Shower', description: 'Plan baby showers and special celebrations', icon: '🎉' },
      { id: 'friends-birthday', title: 'Friends Birthday/Event Planner', description: 'Organize birthday parties and friend gatherings', icon: '🎂' },
    ]
  },
  {
    id: 'finances',
    name: 'Finances',
    description: 'Take control of your money and financial planning',
    color: '#10B981',
    icon: '💰',
    features: [
      { id: 'organise-income', title: 'Organise Income', description: 'Track and manage all your income sources', icon: '💵' },
      { id: 'organise-money', title: 'Organise Money', description: 'Comprehensive financial planning and budgeting', icon: '💸' },
    ]
  },
  {
    id: 'health-fitness',
    name: 'Health, Fitness & Mind',
    description: 'Track your health, fitness goals, and family wellness',
    color: '#F59E0B',
    icon: '💪',
    features: [
      { id: 'exercise', title: 'Exercise', description: 'Track workouts and fitness goals', icon: '💪' },
      { id: '0-2yr-planner', title: '0-2yr Planner', description: 'Track your baby\'s development and milestones', icon: '👶' },
      { id: 'birth-health-plan', title: 'Birth/Health Plan', description: 'Plan for childbirth and health decisions', icon: '🏥' },
      { id: 'hospital-bag', title: 'Hospital Bag', description: 'Pack essentials for hospital stays', icon: '👜' },
      { id: 'looking-after-elderly', title: 'Looking After the Elderly', description: 'Organize care schedules and health tracking', icon: '👴' },
    ]
  },
  {
    id: 'relationships',
    name: 'Relationships',
    description: 'Build and maintain meaningful connections',
    color: '#A78BFA',
    icon: '🤝',
    features: [
      { id: 'should-be-friends', title: 'Should We Be Friends', description: 'Evaluate and strengthen friendships', icon: '🤝' },
    ]
  }
];

function App() {
  const [currentCategory, setCurrentCategory] = useState<string | null>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && categories.find(c => c.id === hash)) {
      setCurrentCategory(hash);
      // Scroll to top when navigating to category via hash
      window.scrollTo({ top: 0, behavior: 'auto' });
    } else {
      setCurrentCategory(null);
    }
  }, []);

  // Listen for hash changes (e.g., browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const categoryIds = categories.map(c => c.id);
      if (hash && categoryIds.includes(hash)) {
        setCurrentCategory(hash);
        // Scroll to top when navigating to category via hash change
        window.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        setCurrentCategory(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryClick = (categoryId: string) => {
    if (categoryId === "" || categoryId === "home") {
      // Clear category and hash
      window.location.hash = '';
      setCurrentCategory(null);
    } else {
      window.location.hash = categoryId;
      setCurrentCategory(categoryId);
      // Scroll to top when navigating to a category page
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleBackToHome = () => {
    window.location.hash = '';
    setCurrentCategory(null);
    // Scroll to categories section
    setTimeout(() => {
      const categoriesSection = document.getElementById('categories');
      if (categoriesSection) {
        categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  if (currentCategory) {
    const category = categories.find(c => c.id === currentCategory);
    if (category) {
      return <CategoryPage 
        category={category} 
        onBack={handleBackToHome}
        categories={categories}
        onCategorySelect={handleCategoryClick}
        currentCategory={currentCategory}
      />;
    }
  }

  return (
    <div className="app">
      {/* Navigation */}
      <nav className={`nav ${scrollY > 50 ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo" onClick={handleBackToHome} style={{ cursor: 'pointer' }}>
            <img src="/icon.png" alt="Systemise Life" className="logo-icon" />
          </div>
          <div className="nav-center">
            <FluidDropdown categories={categories} onCategorySelect={handleCategoryClick} currentCategory={currentCategory} />
          </div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#categories">Categories</a>
            <a href="#pricing">Pricing</a>
            <a href="#download" className="nav-cta">Download Now</a>
          </div>
          {/* Mobile Menu */}
          <MobileMenu>
            <a href="#features">Features</a>
            <a href="#categories">Categories</a>
            <a href="#pricing">Pricing</a>
            <a href="#download" className="nav-cta">Download Now</a>
          </MobileMenu>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="container hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="gradient-text">Systemise</span> Your Entire Life
            </h1>
            <p className="hero-subtitle">
              One app to organize everything. From home management to career planning, 
              travel to finances, and health to relationships. Your complete life organization platform.
            </p>
            <div className="hero-cta">
              <a href="#download" className="btn btn-primary">
                <span>Download Free</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="#categories" className="btn btn-secondary">
                Explore Features
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">6</div>
                <div className="stat-label">Main Categories</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">30+</div>
                <div className="stat-label">Features</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">∞</div>
                <div className="stat-label">Possibilities</div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* Features Overview */}
      <section id="features" className="features-overview">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Everything You Need in One App</h2>
            <p className="section-subtitle">
              Stop juggling multiple apps. Systemise Life brings all your organization tools together in one powerful platform.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Organize Every Aspect of Your Life</h2>
            <p className="section-subtitle">
              Six comprehensive categories covering everything you need to stay organized
            </p>
          </div>

          <div className="categories-grid">
            {categories.map((category) => (
              <div
                key={category.id}
                className="category-card"
                style={{ '--category-color': category.color } as React.CSSProperties}
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className="category-card-content">
                  <div className="category-icon-wrapper">
                    <div className="category-icon" style={{ background: category.color }}>
                      {category.icon}
                    </div>
                  </div>
                  <div className="category-info">
                    <h3 className="category-name">{category.name}</h3>
                    <p className="category-description">{category.description}</p>
                    <div className="category-meta">
                      <span className="category-count">{category.features.length} features</span>
                      <span className="category-arrow">
                        Explore →
                      </span>
                    </div>
                  </div>
                </div>
                <div className="category-hover-effect"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Loved by Thousands</h2>
            <p className="section-subtitle">
              See what our users are saying about Systemise Life
            </p>
          </div>
          <Testimonials />
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">
              Get full access to all features for just £10 per month
            </p>
          </div>
          <div className="pricing-card">
            <div className="pricing-header">
              <div className="pricing-icon">💎</div>
              <h3 className="pricing-title">Premium</h3>
              <p className="pricing-subtitle">Unlock everything</p>
            </div>
            <div className="pricing-price">
              <span className="price-amount">£10</span>
              <span className="price-period">/month</span>
            </div>
            <ul className="pricing-features">
              <li>✓ All 30+ features unlocked</li>
              <li>✓ Unlimited organization tools</li>
              <li>✓ Cloud sync across devices</li>
              <li>✓ Priority support</li>
              <li>✓ Regular feature updates</li>
              <li>✓ No ads, ever</li>
            </ul>
            <a href="#download" className="btn btn-primary pricing-cta">
              Start Free Trial
            </a>
            <p className="pricing-note">7-day free trial, cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download-section">
        <div className="container">
          <div className="download-content">
            <h2 className="download-title">Ready to Systemise Your Life?</h2>
            <p className="download-subtitle">
              Join thousands of users who are already organizing their entire lives with Systemise Life
            </p>
            <div className="download-buttons">
              <a href="#" className="store-button">
                <svg viewBox="0 0 24 24" className="store-icon">
                  <path fill="currentColor" d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="store-text">
                  <span className="store-label">GET IT ON</span>
                  <span className="store-name">Google Play</span>
                </div>
              </a>
              <a href="#" className="store-button">
                <svg viewBox="0 0 24 24" className="store-icon">
                  <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="store-text">
                  <span className="store-label">Download on the</span>
                  <span className="store-name">App Store</span>
                </div>
              </a>
            </div>
            <p className="download-note">Available on iOS and Android</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo">
                <img src="/icon.png" alt="Systemise Life" className="logo-icon" />
              </div>
              <p className="footer-tagline">Organize your entire life in one place</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#categories">Categories</a>
                <a href="#pricing">Pricing</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#">About</a>
                <a href="#">Blog</a>
                <a href="#">Contact</a>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <a href="#">Help Center</a>
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2024 Systemise Life. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
